import { useLayoutEffect } from "react";
import { BRAND } from "@/lib/brand";

/**
 * Dynamic <head> meta tag manager for SEO + social sharing.
 * Updates: <title>, description, canonical, Open Graph, Twitter Card, JSON-LD.
 * Tags are tagged with data-seo so we can clean them up on unmount.
 *
 * Feb 2026 rebrand — brand strings now read from `@/lib/brand` so the
 * customer-facing name / logo / og-image can be flipped by editing
 * one config file. The `SCWT_NAP` export name is preserved (used as
 * an internal identifier by many pages); only the values inside it
 * change to reflect the new "Aware Water Testing" identity.
 */

// Default social share card. Kept pointing at the same URL so historic
// shares don't 404 — replace once we generate an AWARE-branded 1200×630.
const DEFAULT_IMAGE = BRAND.ogImage;
const SITE_NAME = BRAND.name;
const SEO_DEFAULTS_BASE = "https://awarewatertesting.com.au";

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href) {
  if (!href) return;
  // Belt-and-braces: if multiple <link rel="…"> tags of the same
  // relation exist (e.g. one from the HTML shell and one previously
  // injected by React), collapse them to a single element so crawlers
  // never see a duplicate canonical. Feb 2026 — added during sitemap
  // consolidation after removing the incorrect shell canonical.
  const els = Array.from(document.head.querySelectorAll(`link[rel="${rel}"]`));
  let el;
  if (els.length === 0) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "true");
    document.head.appendChild(el);
  } else {
    el = els[0];
    // Remove any duplicates that may have slipped through.
    els.slice(1).forEach((dup) => dup.remove());
  }
  el.setAttribute("href", href);
}

/**
 * Normalise a canonical URL:
 *   • Strip query string (`?utm_…` etc.) — Google guideline.
 *   • Strip fragment (`#section`) — same guideline.
 *   • Preserve trailing slash exactly as the caller supplied it.
 * Passing anything falsy returns undefined so the caller can skip
 * the upsert entirely.
 */
function normaliseCanonical(url) {
  if (!url) return undefined;
  return String(url).split(/[?#]/)[0];
}

/**
 * BlogPosting schema for an article. Kept separate from the FAQPage so
 * both can be emitted side by side (Jun 2026 — the React render carried
 * neither, so prerendered articles had no Article-level structured data
 * at all).
 */
export function buildBlogPostingLd(post, slug) {
  if (!post || !slug) return null;
  const url = `${SEO_DEFAULTS_BASE}/blog/${slug}`;
  const ld = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: String(post.title || "").slice(0, 110),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
    },
  };
  if (post.excerpt) ld.description = String(post.excerpt);
  if (post.featured_image_url) ld.image = post.featured_image_url;
  if (post.published_at) ld.datePublished = String(post.published_at).slice(0, 10);
  if (post.modified_at) ld.dateModified = String(post.modified_at).slice(0, 10);
  return ld.headline ? ld : null;
}

/**
 * Build ONE valid FAQPage from an array of `{question, answer}` rows.
 *
 * Jun 2026 SEO fix: `faq_schema` is stored as plain
 * `{question, answer}` pairs. Passing that array straight into
 * `useSEO({ jsonLd })` emitted each pair as its own <script> with no
 * `@type` and no `@context` — parseable JSON, but invalid structured
 * data (~18 blocks per article, ~540 site-wide). Always wrap.
 *
 * Returns null when there is nothing valid to emit, so we never publish
 * an empty FAQPage.
 */
export function buildFaqPageLd(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const mainEntity = rows
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const name = String(row.question ?? row.name ?? "").trim();
      const text = String(
        row.answer ?? row.acceptedAnswer?.text ?? "",
      ).trim();
      if (!name || !text) return null;
      return {
        "@type": "Question",
        name,
        acceptedAnswer: { "@type": "Answer", text },
      };
    })
    .filter(Boolean);
  if (mainEntity.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

/**
 * Reject anything that is not publishable structured data.
 * Parseable JSON is NOT the same as valid schema.org — a typeless
 * object is worse than no block at all.
 */
function isPublishableLd(entry) {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return false;
  if (entry["@graph"]) return Boolean(entry["@context"]);
  return Boolean(entry["@type"]) && Boolean(entry["@context"]);
}

function upsertJsonLd(id, data) {
  // Remove any previous JSON-LD with the same id then re-insert.
  // Using a stable id (e.g. "localbusiness", "service", "faq") lets
  // pages overwrite their own per-route block without clobbering
  // siblings emitted by the same render.
  const sel = `script[type="application/ld+json"][data-seo="${id}"]`;
  document.head.querySelectorAll(sel).forEach((el) => el.remove());
  if (!data) return;
  const el = document.createElement("script");
  el.setAttribute("type", "application/ld+json");
  el.setAttribute("data-seo", id);
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

// Phase 2L-A129 — shared NAP + LocalBusiness/Organization JSON-LD.
// Single source of truth so per-page schemas can reference the same
// `@id` and Google can collapse the entity graph cleanly. NO review,
// rating, NATA, or accreditation claims (locked guardrail).
// The export name is preserved for backwards-compatibility with the
// ~40 pages that import it; the values inside now reflect the Feb-2026
// Aware Water Testing rebrand.
export const SCWT_NAP = {
  name:      BRAND.name,
  // Feb 2026 — the ABN/registered trading entity is still "Sunshine
  // Coast Water Testing"; the customer-facing brand is "Aware Water
  // Testing". Google's LocalBusiness schema uses `legalName` for the
  // registered entity so we keep the legacy name here for compliance
  // and historic backlinks.
  legalName: "Sunshine Coast Water Testing",
  // The pre-Feb-2026 trading name. Emitted as schema.org
  // `alternateName` so Google treats the old and new names as ONE
  // business rather than two competing entities.
  formerName: "Sunshine Coast Water Testing",
  phone:   "+61 7 5327 3473",
  email:   "noosa@pristinewater.net.au",
  address: {
    streetAddress:   "1/37 Gateway Drive",
    addressLocality: "Noosaville",
    addressRegion:   "QLD",
    postalCode:      "4566",
    addressCountry:  "AU",
  },
  openingHours: ["Mo-Fr 08:30-16:30"],
  areaServed: [
    // Nation-wide primary — Aware Water Testing serves customers
    // Australia-wide via posted / courier samples. Sunshine Coast
    // suburbs kept for local-drop-off SEO relevance.
    "Australia",
    "Sunshine Coast", "Noosa", "Noosaville", "Tewantin", "Cooroy",
    "Pomona", "Eumundi", "Doonan", "Yandina", "Coolum", "Buderim",
    "Maroochydore", "Nambour", "Maleny", "Montville", "Caloundra",
    "Mooloolaba", "Sunshine Coast Hinterland",
  ],
  // Feb 2026 — new AWARE logo. Fallback to the legacy SCWT favicon
  // stays available at the same URL so third-party crawlers that
  // already cached the old asset still resolve.
  // Jun 2026 — single source of truth for the entity logo. Served from
  // our own domain (per brand.js's rule) and MUST match
  // seo_snapshot_service.LOGO so the prerendered HTML and the React
  // render never advertise two different logos for one entity.
  logo:    `${SEO_DEFAULTS_BASE}${BRAND.logos.primaryPng}`,
  image:   DEFAULT_IMAGE,
  url:     "https://awarewatertesting.com.au/",
};

export function buildLocalBusinessLd() {
  return {
    "@context": "https://schema.org",
    "@type":    ["LocalBusiness", "TestingLaboratory"],
    "@id":      `${SCWT_NAP.url}#localbusiness`,
    name:       SCWT_NAP.name,
    // Jun 2026 — entity reconciliation. Google AI Mode was still
    // surfacing "Sunshine Coast Water Testing" as a SEPARATE business
    // because nothing on the site linked the two names. `alternateName`
    // is the standard schema.org signal that a rebranded business is the
    // same entity, not a new one. Do not remove.
    alternateName: SCWT_NAP.formerName,
    legalName:  SCWT_NAP.legalName,
    url:        SCWT_NAP.url,
    image:      SCWT_NAP.image,
    logo:       SCWT_NAP.logo,
    telephone:  SCWT_NAP.phone,
    email:      SCWT_NAP.email,
    address: {
      "@type":         "PostalAddress",
      streetAddress:   SCWT_NAP.address.streetAddress,
      addressLocality: SCWT_NAP.address.addressLocality,
      addressRegion:   SCWT_NAP.address.addressRegion,
      postalCode:      SCWT_NAP.address.postalCode,
      addressCountry:  SCWT_NAP.address.addressCountry,
    },
    geo: { "@type": "GeoCoordinates", latitude: -26.4006, longitude: 153.0590 },
    openingHoursSpecification: [{
      "@type":     "OpeningHoursSpecification",
      dayOfWeek:   ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens:       "08:30", closes: "16:30",
    }],
    areaServed: SCWT_NAP.areaServed.map((n) => ({ "@type": "Place", name: n })),
    // Jun 2026 — brand entity consolidation. See BRAND.socials.
    sameAs: BRAND.socials,
  };
}

export function buildOrganizationLd() {
  return {
    "@context": "https://schema.org",
    "@type":    "Organization",
    "@id":      `${SCWT_NAP.url}#organization`,
    name:       SCWT_NAP.name,
    alternateName: SCWT_NAP.formerName,
    url:        SCWT_NAP.url,
    logo:       SCWT_NAP.logo,
    telephone:  SCWT_NAP.phone,
    email:      SCWT_NAP.email,
    address: {
      "@type":         "PostalAddress",
      streetAddress:   SCWT_NAP.address.streetAddress,
      addressLocality: SCWT_NAP.address.addressLocality,
      addressRegion:   SCWT_NAP.address.addressRegion,
      postalCode:      SCWT_NAP.address.postalCode,
      addressCountry:  SCWT_NAP.address.addressCountry,
    },
    // Jun 2026 — brand entity consolidation. See BRAND.socials.
    sameAs: BRAND.socials,
  };
}

// Jun 2026 SEO audit — cap meta descriptions at 155 chars (word boundary)
// so no template can emit an over-length description.
function truncateDescription(s) {
  if (!s || s.length <= 155) return s;
  const cut = s.slice(0, 155);
  const atWord = cut.slice(0, cut.lastIndexOf(" "));
  return (atWord || cut).replace(/[\s,;:.—-]+$/, "");
}

export function useSEO({
  title,
  description,
  url,
  image = DEFAULT_IMAGE,
  type = "website",
  twitterCard = "summary_large_image",
  noindex = false,
  jsonLd = null,            // single object OR array of objects
  includeBaseline = true,   // emit LocalBusiness + Organization globally
}) {
  // useLayoutEffect (Jun 2026): inject SEO tags BEFORE first paint so
  // crawler snapshots never race the canonical/JSON-LD insertion.
  useLayoutEffect(() => {
    if (title) {
      // Jun 2026 SEO audit — titles must stay ≤ 60 chars. Append the
      // brand suffix only when it fits; never duplicate the brand.
      const hasBrand = title.toLowerCase().includes(SITE_NAME.toLowerCase());
      const withSuffix = `${title} · ${SITE_NAME}`;
      let full = hasBrand || withSuffix.length > 60 ? title : withSuffix;
      if (full.length > 60) {
        const cut = full.slice(0, 60);
        full = (cut.slice(0, cut.lastIndexOf(" ")) || cut).replace(/[\s,;:|·—-]+$/, "");
        // Jun 2026 GSC audit — a 60-char cut used to leave titles ending
        // on a dangling stopword ("… — Water Testing in"). Drop those so
        // the SERP title always reads as a finished phrase.
        let prev = "";
        while (prev !== full) {
          prev = full;
          full = full
            .replace(/\s+(?:in|the|for|and|of|to|a|an|on|with|at|by|from|your|&)$/i, "")
            .replace(/[\s,;:|·—-]+$/, "");
        }
      }
      document.title = full;
      upsertMeta("property", "og:title", full);
      upsertMeta("name", "twitter:title", full);
    }
    if (description) {
      const desc = truncateDescription(description);
      upsertMeta("name", "description", desc);
      upsertMeta("property", "og:description", desc);
      upsertMeta("name", "twitter:description", desc);
    }
    if (url) {
      // Feb 2026 — canonical URLs must not carry query strings or
      // fragments (Google canonicalisation guideline). Normalise once,
      // then use the same cleaned value for both canonical + og:url.
      const cleanUrl = normaliseCanonical(url);
      upsertLink("canonical", cleanUrl);
      upsertMeta("property", "og:url", cleanUrl);
    }
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("name", "twitter:card", twitterCard);
    upsertMeta("name", "twitter:image", image);
    // Draft / preview pages must never be indexed by search engines.
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    // Phase 2L-A129 — JSON-LD baseline (LocalBusiness + Organization)
    // emitted on every public page unless the caller opts out via
    // `includeBaseline: false`. Per-route schemas (Service, FAQPage,
    // Article, BreadcrumbList) ride on top via the `jsonLd` arg.
    // Jun 2026 — the build-time SEO fallback (scripts/seo-fallback.js)
    // injects its own `data-seo-fallback` JSON-LD into <head> so crawlers
    // that never run React still see structured data. Once React mounts
    // it emits the authoritative blocks, so the fallback copies MUST be
    // removed or every page ships TWO Organization nodes (one with @id,
    // one without) — the exact split-entity signal the alternateName /
    // sameAs work exists to prevent.
    document.head
      .querySelectorAll('script[type="application/ld+json"][data-seo-fallback]')
      .forEach((el) => el.remove());

    if (!noindex && includeBaseline) {
      upsertJsonLd("localbusiness", buildLocalBusinessLd());
      upsertJsonLd("organization",  buildOrganizationLd());
    } else {
      upsertJsonLd("localbusiness", null);
      upsertJsonLd("organization",  null);
    }
    if (!noindex && jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((entry, idx) => {
        // Jun 2026 SEO fix — never emit typeless/contextless JSON-LD.
        // Callers must pass real schema.org objects (use
        // buildFaqPageLd() for `{question, answer}` rows).
        if (!isPublishableLd(entry)) {
          if (entry && process.env.NODE_ENV !== "production") {
            console.warn(
              "[useSEO] rejected invalid JSON-LD (needs @context + @type):",
              Object.keys(entry),
            );
          }
          return;
        }
        const id = String(entry["@type"]).toLowerCase();
        upsertJsonLd(`route-${id}-${idx}`, entry);
      });
    }
  }, [title, description, url, image, type, twitterCard, noindex,
      JSON.stringify(jsonLd), includeBaseline]);
}

export const SEO_DEFAULTS = {
  baseUrl: SEO_DEFAULTS_BASE,
  image: DEFAULT_IMAGE,
};
